<template>
  <div>
    <b-button
      v-if="isEligible"
      variant="outline-primary"
      class="button__back-previous ml-u5 mb-u10"
      @click="$_pageNavigationMixin_gotoPreviousPage"
    >
      <span class="mr-2">
        <font-awesome-icon icon="arrow-left" />
      </span>
      Back to previous page
    </b-button>
    <b-container>
      <div v-if="isLoading" class="text-center m-auto">
        <b-spinner v-if="isLoading" label="Loading..." variant="dark" />
      </div>
      <b-form v-else @submit.prevent="submit">
        <b-row class="justify-content-center">
          <b-col cols="12" xs="12" sm="12" md="10" lg="6">
            <div class="text__title h2 font-weight-bold text-left mt-u8 mb-u4">
              {{ textData.title }}
            </div>
            <div v-if="isEligible" class="text__description">{{ textData.description }}</div>
            <div v-else class="text__error">{{ errorMessage }}</div>

            <!-- current Email -->
            <div class="mt-u4">
              <label for="label__current-email" class="mb-u2">
                {{ textData.oldEmail }}
              </label>
              <b-form-input
                id="input__current-email"
                v-model="oldData.email"
                class="input__current-email"
                :disabled="true"
              />
            </div>

            <!-- new Email -->
            <div v-if="isEligible" class="mt-u4">
              <label for="label__email" class="mb-u2"> {{ textData.newEmail }} </label>
              <b-form-input
                id="input__email"
                v-model="form.email"
                :state="errorState('email')"
                class="input__email"
                placeholder="Enter your new email (example@mail.com)"
                autocomplete="off"
                @input="onChangeEmail"
              />
              <div
                v-if="isShowRequiedFeedbackText('email')"
                id="input__email--feedback-required"
                class="text_feedback"
              >
                Email is required.
              </div>

              <div v-if="isInValidEmail" id="input__email--invalid-feedback" class="text_feedback">
                {{ errorMessage }}
              </div>
            </div>

            <!-- password -->
            <div v-if="isEligible" class="mt-u4">
              <label for="label__password" class="mb-u2"> {{ textData.password }} </label>
              <b-form-input
                id="input__password"
                v-model="form.password"
                :novalidate="true"
                :state="errorState('password')"
                type="password"
                class="input__password"
                placeholder="Enter your password"
                debounce="500"
                autocomplete="off"
                @input="onChangePassword"
              />
              <div
                v-if="isShowRequiedFeedbackText('password')"
                id="input__password--feedback-required"
                class="text_feedback"
              >
                Password is required.
              </div>
              <div
                v-if="isInValidPassword"
                id="input__password--feedback-invalid"
                class="text_feedback"
              >
                Password is invalid, please try again.
              </div>
            </div>

            <!-- button -->
            <div v-if="isEligible" class="d-flex my-u10">
              <b-button
                variant="success"
                class="button__confirm"
                type="submit"
                :disabled="isSaving"
                @click="submit"
              >
                <b-spinner v-if="isSaving" style="width: 1rem; height: 1rem" class="" />
                <div v-else>Confirm</div>
              </b-button>
              <b-button
                variant="link"
                class="button__cancel ml-u1"
                @click="$_pageNavigationMixin_gotoPreviousPage"
              >
                Cancel
              </b-button>
            </div>
            <div v-else class="d-flex mt-u5 mb-u10">
              <b-button
                variant="link"
                class="button__back mt-u5"
                @click="$_pageNavigationMixin_gotoPreviousPage"
              >
                Back
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-form>
    </b-container>
  </div>
</template>

<script>
import User from '@/models/User';
import ChangeRequest from '@/models/ChangeRequest';
import { useAuth } from '../../composables/useAuth';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { required } from 'vuelidate/lib/validators';

import { pageNavigationMixin } from '@/mixins/pageNavigationMixin';
import { optimizelyMixin } from '@/mixins/optimizelyMixin';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { checkPassword } = useAuth();

export default {
  components: {
    FontAwesomeIcon
  },

  mixins: [userInfoMixin, pageNavigationMixin, optimizelyMixin],

  props: {
    fallback: {
      type: String,
      default: '/profile'
    }
  },
  data() {
    return {
      textData: {
        title: 'Edit Email',
        description: "After verifying your new email address, it can't be changed for 60 days.",
        oldEmail: 'Current email',
        newEmail: 'New email',
        email: 'Email',
        password: 'Current Password',
        // Keyed by the `reason` code the eligibility endpoint returns. A reason
        // means the block is about the account itself, not the change interval,
        // so there is no date to offer.
        reasonMessages: {
          LinkedGoogleAccount:
            'Your email is managed by your linked Google account and cannot be changed.',
          IdentityCheckUnavailable:
            'Your email cannot be changed right now, please try again later.'
        },
        // Shown when the server blocks the change but offers neither a reason we
        // recognise nor a date. Without it an unknown reason renders the interval
        // sentence with an empty date.
        blockedFallback: 'Your email cannot be changed right now.'
      },
      oldData: {
        email: ''
      },
      form: {
        email: '',
        password: ''
      },
      isEligible: false,
      isSaving: false,
      isLoading: false,
      errorMessage: '',
      isInValidPassword: false,
      isInValidEmail: false,
      latestValidatedPassword: ''
    };
  },
  validations() {
    return {
      form: {
        email: {
          required,
          invalidEmail: () => {
            return this.isInValidEmail === false;
          }
        },
        password: {
          required,
          invalidPassword: () => {
            return this.isInValidPassword === false;
          }
        }
      }
    };
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      this.isLoading = true;

      await Promise.all([User.api().fetchAuth(), this.checkIsEligible()]);

      this.updateUserData();

      this.isLoading = false;
    },

    updateUserData() {
      const { email } = this.user;

      this.$set(this.oldData, 'email', email);
    },

    async submit() {
      this.isSaving = true;
      this.$nextTick(() => {
        this.$v.$reset();
      });
      await this.checkIsEligible();
      if (!this.isEligible) {
        return;
      }
      this.isInValidPassword = false;
      this.isInValidEmail = false;
      this.$v.$touch();
      if (
        !this.$v.form.password.$invalid &&
        this.form.password.length &&
        this.form.password !== this.latestValidatedPassword
      ) {
        let isPasswordCorrect = await this.validatePassword(this.form.password);

        if (isPasswordCorrect) {
          this.isInValidPassword = false;
          this.latestValidatedPassword = this.form.password;
        } else {
          this.isInValidPassword = true;
        }
      }

      const isCompleteForm =
        !this.isErrorFeedbackText('email', 'required') &&
        !this.isErrorFeedbackText('password', 'required') &&
        !this.$v.form.email.$invalid &&
        !this.isInValidPassword;

      if (isCompleteForm) {
        await this.requestChangeEmail();
      }

      this.isSaving = false;
      this.latestValidatedPassword = '';
    },

    async requestChangeEmail() {
      this.errorMessage = '';
      let requestId = '';
      try {
        const {
          response: { data }
        } = await ChangeRequest.api().requestChange({
          changeRequestType: 'ProfileEmailUpdate',
          requestData: {
            currentEmail: this.oldData.email,
            newEmail: this.form.email
          }
        });

        if (data) {
          const {
            data: { id }
          } = data;
          requestId = id;
        }
      } catch (error) {
        this.isInValidEmail = true;
        this.errorMessage = error.response.data.message;
      }

      if (
        this.errorMessage?.length ||
        this.$v.form.password.$invalid ||
        this.isInValidPassword ||
        this.isInValidEmail ||
        this.$v.form.email.$invalid
      ) {
        this.isSaving = false;
        return;
      } else {
        await User.api().fetchAuth();

        this.$_pageNavigationMixin_gotoPreviousPage();

        this.viewPageEmailVerify(requestId);
      }
    },

    viewPageEmailVerify(requestId) {
      const route = this.$router.resolve({ name: 'resend-verify-email', query: { requestId } });
      window.open(route.href, '_blank');
    },

    async checkIsEligible() {
      if (this.$_isFeatureEnabled('change_email') === false) {
        this.errorMessage = `This feature not available yet.`;
        this.isEligible = false;
        return;
      }
      const {
        response: { data }
      } = await ChangeRequest.api().isChangeEmailAble();

      if (!data.eligible) {
        await User.api().fetchAuth();

        this.updateUserData();
      }

      this.isEligible = data.eligible;

      if (!this.isEligible) {
        this.errorMessage = this.eligibilityMessage(data);

        this.isInValidEmail = true;
      }
    },

    eligibilityMessage({ reason, canChangeDate }) {
      if (this.textData.reasonMessages[reason]) {
        return this.textData.reasonMessages[reason];
      }

      if (canChangeDate) {
        return `The email can be changed again on ${canChangeDate}`;
      }

      return this.textData.blockedFallback;
    },

    async validatePassword(password) {
      let { email } = this.user;

      return await checkPassword(email, password);
    },

    onChangePassword() {
      this.isInValidPassword = false;
    },

    onChangeEmail() {
      this.isInValidEmail = false;
    },

    errorState(key) {
      return this.$v.form[key].$dirty
        ? this.$v.form[key].$anyError
          ? !this.$v.form[key].$anyError
          : null
        : null;
    },

    isShowRequiedFeedbackText(dataKey) {
      return this.isErrorFeedbackText(dataKey, 'required');
    },

    isErrorFeedbackText(dataKey, errorKey) {
      return this.$v.form[dataKey].$dirty ? !this.$v.form[dataKey][`${errorKey}`] : false;
    }
  }
};
</script>

<style lang="scss" scoped>
.text__error {
  color: #54371e;
  background-color: #faf3ed;
  padding: 0.5rem 2rem;
  border-radius: 10.4px;
  font-size: 1rem;
  gap: 10px;
  font-weight: 500;
}
.text_feedback {
  width: 100%;
  margin-top: 0.5rem;
  font-size: 80%;
  color: #f83245;
}
li.text__note--list {
  list-style-type: disc;
  margin: 0.5rem 0;
}
</style>
